import { makeStyles } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useCallback, useContext, useEffect, useState } from 'react';
import authAPI, { TractionUser } from '../../api/auth';
import TenantAccountForm, {
  FormValues,
} from '../../components/forms/tenant-account';
import { UserContext } from '../../contexts/user-context';
import PageLoader from '../../components/common/page-loader';
import { PasswordResetModal } from '../../components/common';

const useStyles = makeStyles({
  container: {
    maxWidth: 670,
  },
});

const getInitialValues = (
  currentEmail?: string,
  firstName?: string,
  lastName?: string,
): FormValues => {
  return {
    firstName: firstName || '',
    lastName: lastName || '',
    email: currentEmail || '',
  };
};

function TenantAccountPage() {
  const classes = useStyles();
  const { currentEmail, tokenData, hasAccessToAction } = useContext(UserContext);
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [userData, setUserData] = useState<TractionUser | null>(null);

  const initialValue = getInitialValues(
    currentEmail,
    userData?.firstName,
    userData?.lastName,
  );

  useEffect(() => {
    const loadUserData = async () => {
      if (!tokenData?.id) return;

      const hsaAccessToView = hasAccessToAction('settings.accountSettings.view');

      if (!hsaAccessToView) {
        setUserData(null);
        return;
      }

      try {
        setIsLoadingUser(true);
        const userData = await authAPI.getUser(tokenData.id);
        setUserData(userData);
      } catch (error: any) {
        console.error('Failed to load user data:', error);
        setUserData(null);
      } finally {
        setIsLoadingUser(false);
      }
    };

    loadUserData();
  }, [currentEmail, tokenData?.id, hasAccessToAction]);

  const handleSubmitForm = useCallback(
    async (formValues: FormValues) => {
      try {
        if (!userData) {
          throw new Error('User data is not loaded');
        }

        setIsLoading(true);
        const updatedUser = await authAPI.updateUser({
          ...userData,
          firstName: formValues.firstName,
          lastName: formValues.lastName,
        });

        setUserData(updatedUser);

        enqueueSnackbar('Account information updated successfully', {
          variant: 'success',
        });
        setIsLoading(false);
      } catch (error: any) {
        setIsLoading(false);
        const messageError = `Unfortunately, an error occurred when updating account information.\n Details: ${error.response?.data?.message || 'Internal server error'
          }`;
        enqueueSnackbar(messageError, {
          variant: 'error',
          style: { whiteSpace: 'pre-line' },
        });
        throw error;
      }
    },
    [enqueueSnackbar, userData],
  );

  if (!currentEmail) {
    return <></>;
  }

  if (isLoadingUser) {
    return <PageLoader />;
  }

  return (
    <div className={classes.container}>
      <TenantAccountForm
        loading={isLoading}
        onSubmit={handleSubmitForm}
        initialValue={initialValue}
        onResetPassword={() => setIsPasswordModalOpen(true)}
      />
      <PasswordResetModal
        open={isPasswordModalOpen}
        onClose={() => setIsPasswordModalOpen(false)}
      />
    </div>
  );
}

export default TenantAccountPage;
